// Menuden secmeli custom: SECENEK KURALLARI ve renk kontrasti.
//
// Kural: "EGER <alan> su degerlerden biriyse, <diger alan> su degerleri ALAMAZ"
// (ornek: urun rengi White ise yazi rengi White/Cream olamaz). Kolaj planinda
// her satir bu kurallarla denetlenir; kurallardan alici icin Ingilizce not
// uretilir (Etsy kisisellestirme talimatina eklenir).
//
// Kurala gerek kalmadan da renk kontrasti otomatik denetlenir: yazi/renk
// seceneginin tonu urun rengine (ya da baska bir renk secenegine) cok yakinsa
// baskida gorunmez.

/** Yaygin tisort/sweatshirt renkleri (Gildan/Bella tonlarina yakin) — tasarima
 *  gore urun rengi onerisinde kullanilir */
export const GARMENT_COLORS: Array<{ name: string; hex: string }> = [
  { name: "White", hex: "#ffffff" }, { name: "Natural", hex: "#f3ead7" }, { name: "Sand", hex: "#d8c7a3" },
  { name: "Light Pink", hex: "#f7c6d9" }, { name: "Light Blue", hex: "#a9cdee" }, { name: "Sport Grey", hex: "#a7a7a7" },
  { name: "Red", hex: "#c8102e" }, { name: "Royal", hex: "#2f4fb5" }, { name: "Military Green", hex: "#5a5f3b" },
  { name: "Maroon", hex: "#5b1f2a" }, { name: "Charcoal", hex: "#3d3d3f" }, { name: "Navy", hex: "#1f2d5c" },
  { name: "Black", hex: "#161616" },
];

/** Kuralda urun rengini temsil eden sanal alan anahtari */
export const PRODUCT_COLOR_KEY = "__product_color";

export interface OptionRule {
  id: string;
  ifKey: string;
  ifValues: string[];
  thenKey: string;
  notValues: string[];
  /** Aliciya gosterilecek Ingilizce not; bossa otomatik uretilir */
  noteEn?: string;
}

export interface ComboIssue {
  level: "error" | "warn";
  message: string;
  ruleId?: string;
}

const norm = (s: string) => s.trim().toLowerCase();

function rgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** WCAG kontrast orani (1..21) */
export function contrastRatio(a: string, b: string): number | null {
  const ca = rgb(a), cb = rgb(b);
  if (!ca || !cb) return null;
  const lum = ([r, g, bl]: [number, number, number]) => {
    const f = (v: number) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(bl);
  };
  const la = lum(ca), lb = lum(cb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Baskida gorunmez / zor okunur esikleri (yazi-zemin; WCAG metin esiginden dusuk
 *  cunku baski buyuk ve kalin olur) */
const INVISIBLE = 1.35, WEAK = 1.9;

export interface ComboInput {
  /** fieldKey -> secilen deger (urun rengi PRODUCT_COLOR_KEY ile) */
  values: Record<string, string>;
  /** Renkli ogeler: alan anahtari, Turkce etiket, hex (bilinmiyorsa atlanir) */
  colors: Array<{ key: string; label: string; hex?: string }>;
  /** Urun rengi (ad + hex) */
  product?: { name: string; hex?: string };
  rules?: OptionRule[];
  /** Alan anahtari -> Turkce etiket (mesajlar icin) */
  labels?: Record<string, string>;
}

export function checkOptionCombo(input: ComboInput): ComboIssue[] {
  const issues: ComboIssue[] = [];
  const vals: Record<string, string> = { ...input.values };
  if (input.product?.name) vals[PRODUCT_COLOR_KEY] = input.product.name;
  const label = (k: string) => (k === PRODUCT_COLOR_KEY ? "Ürün rengi" : input.labels?.[k] ?? k);

  for (const r of input.rules ?? []) {
    const a = vals[r.ifKey], b = vals[r.thenKey];
    if (!a || !b) continue;
    if (r.ifValues.some((v) => norm(v) === norm(a)) && r.notValues.some((v) => norm(v) === norm(b))) {
      issues.push({ level: "error", ruleId: r.id, message: `Kural: ${label(r.ifKey)} "${a}" iken ${label(r.thenKey)} "${b}" olamaz.` });
    }
  }

  const colored = input.colors.filter((c) => c.hex && rgb(c.hex));
  const pHex = input.product?.hex;
  if (pHex && rgb(pHex)) {
    for (const c of colored) {
      const cr = contrastRatio(c.hex!, pHex)!;
      if (cr < INVISIBLE) issues.push({ level: "error", message: `${c.label} (${c.hex}) ürün rengiyle (${input.product!.name}) neredeyse aynı — baskıda görünmez.` });
      else if (cr < WEAK) issues.push({ level: "warn", message: `${c.label} ürün rengi ${input.product!.name} üzerinde zor seçilir (kontrast ${cr.toFixed(1)}).` });
    }
  }
  for (let i = 0; i < colored.length; i++) {
    for (let j = i + 1; j < colored.length; j++) {
      const cr = contrastRatio(colored[i].hex!, colored[j].hex!)!;
      if (cr < INVISIBLE) issues.push({ level: "warn", message: `${colored[i].label} ile ${colored[j].label} aynı tonda — iç içe geçip ayırt edilmez.` });
    }
  }
  return issues;
}

/** Kurallardan alici notu (Etsy kisisellestirme talimatina eklenir) */
export function rulesNoteEn(rules: OptionRule[], labelsEn: Record<string, string>): string[] {
  const name = (k: string) => labelsEn[k] ?? k.replace(/^_+/, "").replace(/_/g, " ");
  const list = (v: string[]) => (v.length <= 1 ? v.join("") : `${v.slice(0, -1).join(", ")} or ${v[v.length - 1]}`);
  return rules.map((r) => r.noteEn?.trim()
    || (r.ifKey === PRODUCT_COLOR_KEY
      ? `${list(r.notValues)} ${name(r.thenKey)} is not available on ${list(r.ifValues)} items.`
      : `${list(r.notValues)} ${name(r.thenKey)} is not available with ${list(r.ifValues)} ${name(r.ifKey)}.`));
}
